import { useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';

import { useGetForm } from 'api/hooks/private/forms/GetForm';
import { useUpdateEmailTemplate } from 'api/hooks/private/templates/UpdateEmailTemplate';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog';
import { RichTextEditorProvider } from 'components/Editor/RichTextEditor/RichTextEditorProvider';
import EmailTemplateForm, { EmailTemplateOutputValues } from 'components/TemplateForms';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

interface EmailTemplateProps {
  formId: string;
}

export function EmailTemplate({ formId }: EmailTemplateProps) {
  const dialogs = useDialogs();
  const queryClient = useQueryClient();
  const updateEmailTemplate = useUpdateEmailTemplate();

  // fetch the form
  const formInfo = useGetForm({
    variables: {
      id: formId ?? '',
    },
    enabled: !!formId,
  });

  const onSubmit = async (data: EmailTemplateOutputValues) => {
    try {
      // show confirmation dialog
      const confirmed = await dialogs.confirm(`Update email template for "${formInfo.data?.name}"?`, {
        okText: 'Update',
        cancelText: 'Cancel',
      });

      // if user cancels, return
      if (!confirmed) {
        return;
      }

      const result = await updateEmailTemplate.mutateAsync({
        templateKey: 'form-issued',
        id: formId,
        template: data,
      });

      if (result) {
        // invalidate the query key
        queryClient.invalidateQueries({
          queryKey: ['form', { id: formId }],
        });

        // show success message
        dialogs.open(FeedbackDialog, {
          icon: 'success',
          title: 'Email Template Updated',
        });
      }

      // const editorState = editor.getEditorState();
      // console.log(editorState.toJSON());
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error',
        message: getApiErrorMessage(error),
      });
    }
  };
  return (
    <RichTextEditorProvider>
      <EmailTemplateForm templateKey="form-issued" id={formId} onSubmit={onSubmit} />
    </RichTextEditorProvider>
  );
}

export default EmailTemplate;
